import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { IModalCallback } from "store/common/unsaved-data/unsaved-data-types";

import ConfirmModal from "components/Common/ConfirmModal/ConfirmModal";

interface ComponentProps {
    isUnsavedChangeAvailable: boolean;
    isRefreshActivated: boolean;
    modalConfirmCallbacks: IModalCallback[];
    removeModalConfirmCallback: (
        predicate: (modalCallback: IModalCallback) => boolean
    ) => void;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    setRefreshActivated: (isRefreshActivated: boolean) => void;
}

const UnsavedDataModal: React.FC<ComponentProps> = ({
    isUnsavedChangeAvailable,
    isRefreshActivated,
    modalConfirmCallbacks,
    removeModalConfirmCallback,
    setIsUnsavedChangeAvailable,
    setRefreshActivated,
}: ComponentProps) => {
    const { t } = useTranslation();
    const [currentUrl, setCurrentUrl] = useState("");
    const location = useLocation();
    const clearModalConfirmCallbacks = () => {
        removeModalConfirmCallback(
            (callback) => !callback.isAutoDeleteResistant
        );
    };
    const runModalConfirmCallbacks = () => {
        if (modalConfirmCallbacks.length !== 0) {
            if (modalConfirmCallbacks.some((item) => item.isPageRedirect)) {
                modalConfirmCallbacks
                    .filter((item) => item.isPageRedirect)
                    .forEach((item) => item.callback());
            } else {
                modalConfirmCallbacks.forEach((item) => item.callback());
            }
            clearModalConfirmCallbacks();
        }
        setRefreshActivated(false);
    };

    useEffect(() => {
        window.onpopstate = () => {
            setCurrentUrl(location.pathname);
        };

        return () => {
            window.onpopstate = () => {
                return;
            };
            setCurrentUrl("");
        };
    }, [setCurrentUrl]);
    useEffect(() => {
        // If there are no unsaved changes - just perform the action. No need to show a confirmation modal.
        if (isRefreshActivated && !isUnsavedChangeAvailable) {
            runModalConfirmCallbacks();
        }
    }, [
        isRefreshActivated,
        isUnsavedChangeAvailable,
        runModalConfirmCallbacks,
    ]);
    const confirmButtonOnClickHandler = () => {
        setCurrentUrl("");
        runModalConfirmCallbacks();
        // After confirmation "isUnsavedChangeAvailable" state should be cleared (set to false) in order
        // to prevent the showing of unsaved changes popup after redirection to the new page.
        setIsUnsavedChangeAvailable(false);
    };
    const cancelButtonOnClickHandler = () => {
        if (currentUrl) {
            window.history.replaceState({}, "", currentUrl);
        }
        clearModalConfirmCallbacks();
        setRefreshActivated(false);
    };

    return (
        <ConfirmModal
            cancel={t("unsavedDataModal.button.stay")}
            confirm={t("unsavedDataModal.button.leave")}
            open={
                isRefreshActivated &&
                isUnsavedChangeAvailable &&
                modalConfirmCallbacks.length !== 0
            }
            message={t("unsavedDataModal.mainMessage")}
            onCancel={cancelButtonOnClickHandler}
            onConfirm={confirmButtonOnClickHandler}
        />
    );
};

export default UnsavedDataModal;
